/* ============================================================================
 * SETTINGS.EXE — appearance, and the skin builder
 * ----------------------------------------------------------------------------
 * Two sections, in this order:
 *
 *   APPEARANCE     choose the design language (the three skins)
 *   SKIN BUILDER   adjust that language with the personal knobs
 *
 * The knob vocabulary comes from the Presence "nude" build
 * (docs/NUDE-BUILD.md → apps/web/app/nude/settings.tsx), which is the in-house
 * precedent for personal UI: accent · ground · density · type · borders · hud ·
 * atmosphere. radius and bevel are added here because a desk shell needs them.
 *
 * Every change applies live and persists per browser. A personal setup layers
 * ON TOP of a base skin — set a knob to `canon` and the skin's own value wins —
 * which is what makes the setup portable when you switch design language.
 *
 * Precedent followed: Export / Import JSON and Reset to canon, as nude does.
 * ========================================================================= */

import { useMemo, useState } from 'react';
import { useAppearance } from '../components/AppearanceProvider';
import { FUNCTION_MARKS, FunctionMark } from '../components/icons';
import { KNOBS, countOverrides, type KnobKey } from '../lib/appearance';
import { SKIN_DEFS, SKIN_LABEL, ROLE_LABEL } from '../hooks/useSkin';

export function Settings() {
  const a = useAppearance();
  const [importText, setImportText] = useState('');
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // The controller's setter is generic; the UI iterates heterogeneous knobs.
  const setKnob = (key: KnobKey, value: string) =>
    (a.set as (k: KnobKey, v: string) => void)(key, value);

  const exported = useMemo(() => a.exportJson(), [a]);

  const doExport = async () => {
    try {
      await navigator.clipboard.writeText(exported);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
      setImportMsg('Clipboard blocked — select the JSON below instead.');
    }
  };

  const doImport = () => {
    const res = a.importJson(importText.trim());
    setImportMsg(res.ok ? 'Imported.' : `Not imported — ${res.error}`);
    if (res.ok) setImportText('');
  };

  return (
    <div className="stack">
      {/* ── APPEARANCE ─────────────────────────────────────────────────── */}
      <span className="eyebrow">appearance — design language</span>
      <p className="note">
        One main skin, the origin it came from, and forks for verticals. A fork
        inherits the main skin until it is differentiated.
      </p>
      <div className="panel">
        {SKIN_DEFS.map((d) => (
          <button
            key={d.id}
            type="button"
            className="btn"
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              marginBottom: 4,
              whiteSpace: 'normal',
              ...(a.skin === d.id
                ? { borderColor: 'var(--ui-accent)', color: 'var(--ui-accent)' }
                : {}),
            }}
            onClick={() => a.setSkin(d.id)}
          >
            {a.skin === d.id ? '● ' : '○ '}
            {d.label}
            <span
              className={`tag ${d.role === 'master' ? 'tag--ok' : 'tag--muted'}`}
              style={{ marginLeft: 6, textTransform: 'uppercase' }}
            >
              {ROLE_LABEL[d.role]}
            </span>
            {!d.designed && (
              <span className="tag tag--warn" style={{ marginLeft: 4 }}>
                inherits {d.forksFrom ? SKIN_LABEL[d.forksFrom] : '—'}
              </span>
            )}
            <span className="note" style={{ display: 'block', textTransform: 'none' }}>
              {d.note}
            </span>
          </button>
        ))}
      </div>

      <hr className="rule" />

      {/* ── SKIN BUILDER ───────────────────────────────────────────────── */}
      <div className="hstack" style={{ justifyContent: 'space-between' }}>
        <span className="eyebrow">skin builder — personal</span>
        <span className="tag tag--muted">{a.overrides} / {KNOBS.length} set</span>
      </div>

      <p className="note">
        Personal overrides layer on top of <strong>{SKIN_LABEL[a.skin]}</strong>.
        Set a knob to <em>canon</em> and the design language&rsquo;s own value wins,
        so switching language keeps the rest of your setup.
      </p>

      {KNOBS.map((knob) => {
        const current = (a.appearance as unknown as Record<string, string>)[knob.key];
        return (
          <div key={knob.key} className="panel">
            <div className="hstack" style={{ justifyContent: 'space-between' }}>
              <span className="label" style={{ margin: 0 }}>{knob.label}</span>
            </div>
            <div className="hstack" style={{ marginTop: 6, gap: 4 }}>
              {knob.options.map((opt) => {
                const on = current === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    className="btn"
                    title={opt.value}
                    style={{
                      padding: '3px 8px',
                      fontSize: 10,
                      ...(on
                        ? {
                            borderColor: 'var(--ui-accent)',
                            color: 'var(--ui-accent)',
                            fontWeight: 700,
                          }
                        : {}),
                    }}
                    onClick={() => setKnob(knob.key, opt.value)}
                  >
                    {opt.swatch && (
                      <span
                        aria-hidden="true"
                        style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          marginRight: 5,
                          background: opt.swatch,
                          border: '1px solid var(--ui-border)',
                          verticalAlign: 'middle',
                        }}
                      />
                    )}
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <p className="note" style={{ marginTop: 5 }}>{knob.note}</p>
          </div>
        );
      })}

      <hr className="rule" />

      {/* ── ICON SET ───────────────────────────────────────────────────── */}
      <span className="eyebrow">icon set — one dish, sixteen functions</span>
      <p className="note">
        The suite's marks, as the active skin renders them. Each desk in the
        dock owns one; the mark never changes, the colour does. 24 px box,
        1.5 px stroke, never a filled pictogram.
      </p>
      <div className="panel">
        {FUNCTION_MARKS.map((mark) => (
          <div key={mark.id} className="iconset__row">
            <FunctionMark name={mark.id} size={16} className="iconset__mark" />
            <span className="iconset__label">{mark.label}</span>
            <span className="iconset__app mono">{mark.appId}</span>
          </div>
        ))}
      </div>

      <hr className="rule" />

      {/* ── PORTABILITY ────────────────────────────────────────────────── */}
      <span className="eyebrow">portability</span>
      <div className="hstack">
        <button type="button" className="btn" onClick={doExport}>
          {copied ? 'Copied' : 'Export JSON'}
        </button>
        <button
          type="button"
          className="btn"
          onClick={a.reset}
          disabled={a.overrides === 0}
        >
          Reset to canon
        </button>
      </div>

      <textarea
        readOnly
        value={exported}
        rows={6}
        spellCheck={false}
        style={{
          background: 'var(--ui-well, var(--ui-surface-2))',
          border: '1px solid var(--ui-border-dim)',
          borderRadius: 'var(--ui-radius-ctl)',
          padding: '6px 8px',
          fontSize: 10,
          fontFamily: 'var(--ui-mono, monospace)',
          resize: 'vertical',
        }}
      />

      <span className="eyebrow">import</span>
      <textarea
        value={importText}
        onChange={(e) => setImportText(e.target.value)}
        rows={4}
        spellCheck={false}
        placeholder="Paste an exported appearance to apply it"
        style={{
          background: 'var(--ui-well, var(--ui-surface-2))',
          border: '1px solid var(--ui-border-dim)',
          borderRadius: 'var(--ui-radius-ctl)',
          padding: '6px 8px',
          fontSize: 10,
          fontFamily: 'var(--ui-mono, monospace)',
          resize: 'vertical',
        }}
      />
      <div className="hstack">
        <button
          type="button"
          className="btn"
          onClick={doImport}
          disabled={importText.trim().length === 0}
        >
          Apply
        </button>
        {importMsg && <span className="note">{importMsg}</span>}
      </div>

      <hr className="rule" />

      <div className="panel">
        <div className="row">
          <span className="row__k">Base skin</span>
          <span className="row__v">{SKIN_LABEL[a.skin]}</span>
        </div>
        <div className="row">
          <span className="row__k">Overrides</span>
          <span className="row__v">
            {countOverrides(a.appearance)} of {KNOBS.length}
          </span>
        </div>
        <div className="row">
          <span className="row__k">Stored</span>
          <span className="row__v mono">
            supercompute-webos:appearance:v1
          </span>
        </div>
      </div>

      <p className="note note--warn">
        A personal setup is <strong>local to this browser</strong> and is never
        published — the same contract the nude build uses. Export the JSON if you
        want it to travel.
      </p>
    </div>
  );
}
